// Diagnose Mode: VDR multi-session (Mode 3) only.
// In-memory fixture for the /vdr/project/* backend surface, used by the local mock interceptor.
// Two pre-populated demo projects let devs exercise every mockup screen without a live backend.
import { Injectable, signal } from '@angular/core';
import {
  VdrProjectCreateSessionPayload,
  VdrProjectInitializePayload,
  VdrProjectSession,
  VdrProjectSessionList,
  VdrProjectUpdatePslPayload,
  VdrPsl,
  VdrSessionRenamePayload,
} from '../common/models/workflow/mode3-project.model';

interface MockProject {
  projectId: string;
  referenceId: string;
  projectName: string;
  pslCode: string | null;
  serviceLine: string | null;
}

const NOW = () => new Date().toISOString();

// Safe Guid helper — jsdom in older Node builds may not expose crypto.randomUUID.
function newGuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Well-known Guid so devs can hit `/pid/<id>` deterministically. */
export const DEMO_PROJECT_EMPTY = '11111111-1111-1111-1111-111111111111';
export const DEMO_PROJECT_POPULATED = '22222222-2222-2222-2222-222222222222';

@Injectable({ providedIn: 'root' })
export class Mode3MockStore {
  readonly projects = signal<Map<string, MockProject>>(this.#seedProjects());
  readonly sessionsByProject = signal<Map<string, VdrProjectSession[]>>(
    this.#seedSessions(),
  );

  readonly psls: VdrPsl[] = [
    { code: 'PPS', name: 'Production Solutions', description: '' },
    { code: 'PS', name: 'Pipeline & Process Services', description: '' },
    { code: 'STI', name: 'Stimulation', description: '' },
    { code: 'CT', name: 'Coiled Tubing', description: '' },
  ];

  // --- API 1 ---------------------------------------------------------------

  initializeProject(payload: VdrProjectInitializePayload): string {
    const projectId = newGuid();
    const project: MockProject = {
      projectId,
      referenceId: payload.projectId,
      projectName: payload.projectName,
      pslCode: payload.pslCode ?? null,
      serviceLine: this.#lookupServiceLine(payload.pslCode ?? null),
    };
    this.projects.update((m) => new Map(m).set(projectId, project));
    this.sessionsByProject.update((m) => new Map(m).set(projectId, []));
    return projectId;
  }

  // --- API 3 ---------------------------------------------------------------

  getProjectSessions(projectId: string, status?: string): VdrProjectSessionList | null {
    const project = this.projects().get(projectId);
    if (!project) return null;

    const raw = this.sessionsByProject().get(projectId) ?? [];
    const filter = (status ?? 'Diagnosis Completed').toLowerCase();
    const sessions = filter === 'all' ? raw : raw.filter((s) => s.status.toLowerCase() === filter);

    return { ...project, sessions };
  }

  // --- API 4 ---------------------------------------------------------------

  lockSession(sessionId: string, isLocked: boolean): { ok: boolean; message?: string } {
    return this.#mutateSession(sessionId, (session) => {
      if (isLocked && session.status !== 'Diagnosis Completed') {
        return { ok: false, message: `Session [${sessionId}] must be in status [Diagnosis Completed] to be locked. Current status: [${session.status}].` };
      }
      if (!isLocked && session.status !== 'Approved for Design') {
        return { ok: false, message: `Session [${sessionId}] must be in status [Approved for Design] to be unlocked. Current status: [${session.status}].` };
      }
      session.status = isLocked ? 'Approved for Design' : 'Diagnosis Completed';
      session.isLocked = isLocked;
      session.modifiedAt = NOW();
      return { ok: true };
    });
  }

  // --- Support endpoints ---------------------------------------------------

  updateProjectPsl(projectId: string, payload: VdrProjectUpdatePslPayload): { ok: boolean; message?: string } {
    const project = this.projects().get(projectId);
    if (!project) return { ok: false, message: `No project exists for the provided projectId [${projectId}].` };
    if (project.pslCode) return { ok: false, message: `Project [${projectId}] already has PslCode [${project.pslCode}].` };
    if (!this.psls.some((p) => p.code === payload.pslCode)) {
      return { ok: false, message: `Data retrieval failed for the given PslCode [${payload.pslCode}].` };
    }
    const updated: MockProject = { ...project, pslCode: payload.pslCode, serviceLine: this.#lookupServiceLine(payload.pslCode) };
    this.projects.update((m) => new Map(m).set(projectId, updated));
    return { ok: true };
  }

  createSession(projectId: string, payload: VdrProjectCreateSessionPayload): VdrProjectSession | null {
    if (!this.projects().get(projectId)) return null;
    const source = payload.copyFromSessionId
      ? (this.sessionsByProject().get(projectId) ?? []).find((s) => s.sessionId === payload.copyFromSessionId)
      : undefined;

    const session: VdrProjectSession = {
      sessionId: newGuid(),
      dxId: `DX-${new Date().getFullYear()}-${newGuid().slice(0, 6).toUpperCase()}`,
      title: payload.title,
      status: 'In-Progress',
      isLocked: false,
      customerChallenges: source ? [...source.customerChallenges] : [],
      subChallenges: source ? [...source.subChallenges] : [],
      customerSpecifications: source ? [...source.customerSpecifications] : [],
      recommendedSolutions: [],
      chemicals: [],
      tools: [],
      conveyanceMethod: [],
      completionDate: null,
      createdAt: NOW(),
      createdBy: payload.createdBy,
      modifiedAt: NOW(),
      modifiedBy: payload.createdBy,
    };
    this.sessionsByProject.update((m) => {
      const next = new Map(m);
      const list = [...(next.get(projectId) ?? []), session];
      next.set(projectId, list);
      return next;
    });
    return session;
  }

  renameSession(sessionId: string, payload: VdrSessionRenamePayload): VdrProjectSession | null {
    let renamed: VdrProjectSession | null = null;
    this.#mutateSession(sessionId, (session) => {
      if (session.isLocked) return { ok: false, message: `Session [${sessionId}] is locked because it is Approved for Design.` };
      session.title = payload.title;
      session.modifiedBy = payload.modifiedBy;
      session.modifiedAt = NOW();
      renamed = session;
      return { ok: true };
    });
    return renamed;
  }

  deleteSession(sessionId: string): { ok: boolean; message?: string } {
    // NB: cannot delegate to #mutateSession — that helper re-inserts the mutated clone after
    // the mutator returns, which would undo the filter below. Delete has to walk the map itself.
    for (const [pid, list] of this.sessionsByProject()) {
      const session = list.find((s) => s.sessionId === sessionId);
      if (!session) continue;
      if (session.isLocked) {
        return { ok: false, message: `Session [${sessionId}] is locked because it is Approved for Design.` };
      }
      this.sessionsByProject.update((m) => {
        const next = new Map(m);
        next.set(pid, list.filter((s) => s.sessionId !== sessionId));
        return next;
      });
      return { ok: true };
    }
    return { ok: false, message: `Session [${sessionId}] was not found.` };
  }

  // --- Internals -----------------------------------------------------------

  #mutateSession(
    sessionId: string,
    mutator: (session: VdrProjectSession) => { ok: boolean; message?: string },
  ): { ok: boolean; message?: string } {
    let result: { ok: boolean; message?: string } = { ok: false, message: `Session [${sessionId}] was not found.` };
    this.sessionsByProject.update((m) => {
      const next = new Map(m);
      for (const [pid, list] of next) {
        const idx = list.findIndex((s) => s.sessionId === sessionId);
        if (idx >= 0) {
          const clone = { ...list[idx] };
          result = mutator(clone);
          if (result.ok) {
            const newList = [...list];
            newList[idx] = clone;
            next.set(pid, newList);
          }
          break;
        }
      }
      return next;
    });
    return result;
  }

  #lookupServiceLine(pslCode: string | null): string | null {
    if (!pslCode) return null;
    return this.psls.find((p) => p.code === pslCode)?.name ?? null;
  }

  #seedProjects(): Map<string, MockProject> {
    return new Map<string, MockProject>([
      [DEMO_PROJECT_EMPTY, {
        projectId: DEMO_PROJECT_EMPTY,
        referenceId: 'MOB-PRJ-1000',
        projectName: 'Fresh Mode 3 Project (no PSL yet)',
        pslCode: null,
        serviceLine: null,
      }],
      [DEMO_PROJECT_POPULATED, {
        projectId: DEMO_PROJECT_POPULATED,
        referenceId: 'MOB-PRJ-3525',
        projectName: 'Eric\'s 3DW Demo',
        pslCode: 'PPS',
        serviceLine: 'Production Solutions',
      }],
    ]);
  }

  #seedSessions(): Map<string, VdrProjectSession[]> {
    const now = NOW();
    return new Map<string, VdrProjectSession[]>([
      [DEMO_PROJECT_EMPTY, []],
      [DEMO_PROJECT_POPULATED, [
        {
          sessionId: '5a2f9c8d-1a2b-4c3d-9e4f-6a7b8c9d0e1f',
          dxId: 'DX-2026-A3F2B1',
          title: 'Initial Diagnosis',
          status: 'In-Progress',
          isLocked: false,
          customerChallenges: ['Milling', 'Wellbore Cleanout'],
          subChallenges: ['Milling Scale', 'Cleanout - Organic Deposits'],
          customerSpecifications: [],
          recommendedSolutions: [],
          chemicals: [],
          tools: [],
          conveyanceMethod: [],
          completionDate: null,
          createdAt: now,
          createdBy: 'demo@halliburton.com',
          modifiedAt: now,
          modifiedBy: 'demo@halliburton.com',
        },
        {
          sessionId: '9f7a2b31-4c5d-6e7f-8a9b-0c1d2e3f4a5b',
          dxId: 'DX-2026-B71C43',
          title: 'Acid Blend Trial',
          status: 'Approved for Design',
          isLocked: true,
          customerChallenges: ['Milling', 'Wellbore Cleanout'],
          subChallenges: ['Milling Scale', 'Cleanout - Organic Deposits'],
          customerSpecifications: ['Product Handled: Cooling Water'],
          recommendedSolutions: ['CT', 'N-Ver-Sperse-0', 'Pulsonix TFA', 'SPECTRUM', 'SPECTRUM 360'],
          chemicals: ['N-Ver-Sperse-0', 'Pulsonix TFA'],
          tools: ['SPECTRUM', 'SPECTRUM 360'],
          conveyanceMethod: ['CT'],
          completionDate: now,
          createdAt: now,
          createdBy: 'demo@halliburton.com',
          modifiedAt: now,
          modifiedBy: 'demo@halliburton.com',
        },
        {
          sessionId: '4a1c2d3e-4f5a-6b7c-8d9e-0f1a2b3c4d5e',
          dxId: 'DX-2026-C90D18',
          title: 'Pulsed Bypass Option',
          status: 'Diagnosis Completed',
          isLocked: false,
          customerChallenges: ['Milling', 'Wellbore Cleanout'],
          subChallenges: ['Milling Scale', 'Cleanout - Organic Deposits'],
          customerSpecifications: ['Product Handled: Cooling Water'],
          recommendedSolutions: ['CT', 'N-Ver-Sperse-0', 'Pulsonix TFA', 'SPECTRUM', 'SPECTRUM 360'],
          chemicals: ['N-Ver-Sperse-0', 'Pulsonix TFA'],
          tools: ['SPECTRUM', 'SPECTRUM 360'],
          conveyanceMethod: ['CT'],
          completionDate: now,
          createdAt: now,
          createdBy: 'demo@halliburton.com',
          modifiedAt: now,
          modifiedBy: 'demo@halliburton.com',
        },
      ]],
    ]);
  }
}
